import {
  EntryTypeEnum,
  MedicalCategory,
  OrderDetail,
  SportCategoryEnum,
  TravelCategoryEnum
} from '@insurance/api/requests'
import { getEffectiveDateRange } from '@insurance/utils'
import { usePermissionRestriction } from '@shared/hooks'
import { Card, Col, Divider, Form, Modal, Row } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AlertModifyInsurance } from './AlertModifyInsurance'
import { CustomerStep } from './CustomerStep'
import { Details } from './Details'
import { HealthInsuranceProvider } from './HealthInsuranceContext'
import { HealthInsuranceControls } from './HealthInsuranceControls'
import { MainStep } from './MainStep'
import { PayForm } from './PayForm'
import {
  getHealthValuesFromStorage,
  INSURANCE_DURATION_MIN_DAYS,
  removeHealthValuesFromStorage,
  saveHealthValuesToStorage
} from './utils'
import {
  iniMixtContractorValues,
  serializeContractorFields
} from '../PersonsFields'

const forms = [
  <MainStep key={0} />,
  <PayForm key={1} />,
  <CustomerStep key={2} />
]

const initValues = {
  price: undefined,
  step: 0,
  order_id: undefined,
  in_country: true,
  entry_type: EntryTypeEnum.SINGLE,
  travel_category: TravelCategoryEnum.TOURISM_BUSINESS_STUDIES,
  sport_category: SportCategoryEnum.CATEGORY_1,
  covid: true,
  valid_from: dayjs(),
  valid_to: dayjs().add(INSURANCE_DURATION_MIN_DAYS - 1, 'd'),
  count_days: INSURANCE_DURATION_MIN_DAYS,
  term: 2,
  persons: [{ date_of_birth: undefined, resident: true }],
  category: MedicalCategory.A,
  contractor: iniMixtContractorValues.contractor,
  same_contractor_as_first_person: true,
  directions: [],
  customer: undefined,
  effective_date: undefined
}

type Props = {
  order?: OrderDetail
}

export const HealthInsurance = ({ order }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)
  const { hasPermission } = usePermissionRestriction()

  const saveOnChangeValue = () => {
    const values = form.getFieldsValue(true)

    saveHealthValuesToStorage(values, order?.id)

    return null
  }

  const formattedInitValues = useMemo(() => {
    const detailValues = order?.medical_travel

    if (!detailValues) return initValues

    const insuredDays =
      dayjs(detailValues.valid_to).diff(detailValues.valid_from, 'd') + 1

    const effectiveDateRange = getEffectiveDateRange(order)

    serializeContractorFields({ hasPermission }, detailValues)

    return {
      ...initValues,
      ...detailValues,
      ...(detailValues.valid_from && {
        valid_from: dayjs(detailValues.valid_from)
      }),
      ...(detailValues.valid_to && {
        valid_to: dayjs(detailValues.valid_to)
      }),
      effective_date: effectiveDateRange.minDate,
      persons: detailValues.persons.map((person) => ({
        ...person,
        date_of_birth: dayjs(person.date_of_birth),
        document: [person.document?.series, person.document?.number]
          .filter(Boolean)
          .join('')
      })),
      directions: detailValues.directions.map(({ id }) => id),
      insurance: detailValues.insurance.id,
      customer: order.user,
      order_price: { amount: order?.full_paid_amount },
      count_days: insuredDays
    }
  }, [order])

  useEffect(() => {
    form.setFieldsValue({
      ...formattedInitValues,
      ...getHealthValuesFromStorage(order?.id)
    })
  }, [formattedInitValues, form, order?.id])

  useEffect(() => {
    const values = getHealthValuesFromStorage(order?.id)
    if (!values) return

    const modal = Modal.confirm({
      onCancel: () => {
        removeHealthValuesFromStorage(order?.id)
        setIsReady(false)
        form.setFieldsValue(formattedInitValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => modal.destroy()
  }, [form, t, formattedInitValues, order?.id])

  const handleReadyToSaveData = () => setIsReady(true)

  return (
    <HealthInsuranceProvider isEditable={!!order} order={order}>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <AlertModifyInsurance />

          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => <Card>{forms[form.getFieldValue('step')]}</Card>}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <HealthInsuranceControls
          steps={forms.length}
          onReadyToSaveData={handleReadyToSaveData}
        />

        {/* use this because when change step and other preserve values doesn't trigger onValuesChange */}
        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </HealthInsuranceProvider>
  )
}
